import { Injectable, inject } from '@angular/core';
import { createDistributedTraceContext } from '@microsoft/applicationinsights-core-js';
import { ApplicationInsights, IOTelAttributes } from '@microsoft/applicationinsights-web';
import { IoConfigurationFeatureCodes } from '@omni/classes/authentication/user.class';
import { DB_KEY_PREFIXES } from '@omni/config/pouch-db.config';
import { AuthenticationDataService } from '@omni/data-services/authentication/authentication.service';
import { AuthenticationService } from '@omni/services/authentication.service';
import { DiskService } from '@omni/services/disk/disk.service';
import { TrackService } from '@omni/services/logging/tracking.service';
import { TurnUsageRecord } from '../transports/ai-transport';

/**
 * Maximum UTF-16 code units the web SDK keeps in one custom-property value, which a span attribute becomes;
 * `DataSanitizerValues.MAX_PROPERTY_LENGTH` in `@microsoft/applicationinsights-core-js`.
 */
export const PROPERTY_VALUE_CHARS = 8192;

/** `eOTelSpanKind.CLIENT` in `@microsoft/applicationinsights-core-js`. */
const SPAN_KIND_CLIENT = 2;

/** `eOTelSpanStatusCode.ERROR` in `@microsoft/applicationinsights-core-js`. */
const SPAN_STATUS_ERROR = 2;

/** W3C trace flags of a sampled trace, as sent in the turn's traceparent. */
const TRACE_FLAGS_SAMPLED = 1;

/** `gen_ai.provider.name` of Foundry Agent Service, the value Foundry stamps on its own `invoke_agent` spans. */
const PROVIDER_NAME = 'microsoft.foundry';

/**
 * Span attributes the web SDK leaves out of a dependency's custom properties: every `microsoft.*` key and
 * `error.type`. The telemetry initializer writes them onto the item.
 */
const SDK_OMITTED_ATTRIBUTES = ['microsoft.channel.name', 'error.type'] as const;

/** Channel value for turns requested from the app. */
const APP_CHANNEL = 'app';

/** Entra resource for the Foundry data plane; `getTokenForResource` appends `/.default`. */
const FOUNDRY_RESOURCE = 'https://ai.azure.com';

/** Foundry data-plane API version that lists a project's connections. */
const CONNECTIONS_API_VERSION = '2025-11-15-preview';

/**
 * Storage name prefix for the dedicated instances. The web SDK keeps unsent telemetry in session storage
 * under fixed key names, so an instance without its own prefix also sends the app instance's buffered items.
 */
const STORAGE_NAME_PREFIX = 'nova_ai';

/**
 * Reserved custom-property keys that carry envelope tags from {@link AiTelemetryService.track} to the
 * telemetry initializer, which moves them onto `ai.operation.id` and `ai.user.authUserId` and strips them.
 * The web SDK sources both tags of an event from its own context, not from custom properties.
 */
const OPERATION_ID = '__operationId';
const AUTH_USER_ID = '__authUserId';

/** One AI telemetry event. */
export interface AiTelemetryEvent {
  name: string;
  /** Custom dimensions; values are strings so they stay in `customDimensions` rather than `customMeasurements`. */
  properties: Record<string, string>;
  /** W3C trace id the event belongs to; becomes the envelope's operation id. */
  operationId?: string;
}

/** Structural view of a Foundry project connection, limited to the fields read here. */
interface ProjectConnection {
  type?: string;
  metadata?: { ApplicationInsightsConnectionString?: string };
}

/** Structural view of the telemetry-initializer item touched here; `baseData.id` of a span's item is its span id. */
interface TelemetryItem {
  baseData?: { id?: unknown; properties?: Record<string, unknown> };
  data?: Record<string, unknown>;
  tags?: Record<string, unknown>;
}

/** Local document holding the connection string resolved for each Foundry project endpoint. */
interface ConnectionStringDoc {
  raw?: Record<string, string>;
}

/** One message of the OpenTelemetry GenAI `gen_ai.input.messages` and `gen_ai.output.messages` attributes. */
interface ChatMessage {
  role: 'user' | 'assistant';
  parts: Array<{ type: 'text'; content: string }>;
  finish_reason?: string;
}

/**
 * Writes AI telemetry (one `invoke_agent` span per turn, and feedback events) to the App Insights resource
 * registered on the Foundry project that served the turn, so it sits beside the agent's own traces and
 * names the Dataverse user as the authenticated user.
 *
 * The resource for a project endpoint is, in order: the connection string saved on the device for that
 * endpoint; the project's `AppInsights` connection read from Foundry, which is then saved; the
 * `ORG_AI_APPINSIGHTS_CONNECTION_STRING` setting; and otherwise the app's own App Insights instance from
 * the global tenant configuration. On that instance only telemetry written here is altered, by an initializer
 * that acts on the reserved keys and on the spans started here. Writing never throws.
 */
@Injectable({ providedIn: 'root' })
export class AiTelemetryService {
  readonly #auth = inject(AuthenticationService);
  readonly #authData = inject(AuthenticationDataService);
  readonly #disk = inject(DiskService);
  readonly #track = inject(TrackService);
  /** Project endpoint → its dedicated instance, or undefined for the app's own instance; resolved once per session. */
  readonly #sinks = new Map<string, Promise<ApplicationInsights | undefined>>();
  /** Connection string → instance, so projects sharing a resource share one instance. */
  readonly #instances = new Map<string, ApplicationInsights>();
  readonly #initialized = new WeakSet<ApplicationInsights>();
  /** Span id → the {@link SDK_OMITTED_ATTRIBUTES} of a span started here, until its telemetry item is initialized. */
  readonly #omittedAttributes = new Map<string, Record<string, string>>();

  /**
   * Writes one finished turn of an agent hosted at `endpoint` as a CLIENT `invoke_agent {agent}` span with
   * the OpenTelemetry GenAI attributes. The span is a child of the turn's traceparent, so it shares the
   * agent's operation id, and it runs from the turn's start for its measured duration. A failed turn has
   * status Error and `error.type`.
   */
  async trackTurn(endpoint: string | undefined, turn: TurnUsageRecord): Promise<void> {
    try {
      const instance = await this.#instanceForTurn(endpoint);
      if (!instance?.startSpan) return;
      this.#ensureInitializer(instance);
      const parent = createDistributedTraceContext({
        traceId: turn.traceId,
        spanId: turn.parentSpanId,
        traceFlags: TRACE_FLAGS_SAMPLED,
      });
      const attributes = this.#turnAttributes(turn);
      const span = instance.startSpan(
        `invoke_agent ${turn.agentName}`,
        { kind: SPAN_KIND_CLIENT, startTime: new Date(turn.startTime), attributes },
        parent,
      );
      if (!span) return;
      if (turn.outcome === 'failure') span.setStatus({ code: SPAN_STATUS_ERROR });
      const spanId = span.spanContext().spanId;
      const omitted: Record<string, string> = {};
      for (const key of SDK_OMITTED_ATTRIBUTES) {
        if (attributes[key] !== undefined) omitted[key] = String(attributes[key]);
      }
      this.#omittedAttributes.set(spanId, omitted);
      try {
        span.end(new Date(turn.startTime + Math.max(0, turn.durationMs)));
      } finally {
        this.#omittedAttributes.delete(spanId);
      }
    } catch (e) {
      console.warn('AiTelemetry: the turn span could not be written', e);
    }
  }

  /** The OpenTelemetry attributes of a turn's span; undefined and empty values are left out. */
  #turnAttributes(turn: TurnUsageRecord): IOTelAttributes {
    const { usage } = turn;
    const attributes: IOTelAttributes = {
      'gen_ai.operation.name': 'invoke_agent',
      'gen_ai.provider.name': PROVIDER_NAME,
      'gen_ai.agent.name': turn.agentName,
      'gen_ai.agent.version': turn.agentVersion,
      'gen_ai.agent.id': turn.agentVersion ? `${turn.agentName}:${turn.agentVersion}` : turn.agentName,
      'gen_ai.conversation.id': turn.conversationId,
      'gen_ai.request.model': turn.model,
      'gen_ai.response.id': turn.responseId,
      'gen_ai.usage.input_tokens': usage.inputTokens,
      'gen_ai.usage.output_tokens': usage.outputTokens,
      'gen_ai.usage.cache_read.input_tokens': usage.cachedInputTokens ?? 0,
      'gen_ai.usage.reasoning.output_tokens': usage.reasoningTokens ?? 0,
      'microsoft.channel.name': APP_CHANNEL,
      'error.type': turn.outcome === 'failure' ? turn.errorType || '_OTHER' : undefined,
      'enduser.id': this.#auth.user?.systemUserID?.toLowerCase() || undefined,
      'com.exeevo.dataverse.org': hostOf(this.#auth.userConfig?.activeInstance?.url) || undefined,
      'com.exeevo.origin': turn.origin,
      ...contentAttributes(turn.question, turn.answer),
    };
    for (const key of Object.keys(attributes)) {
      if (attributes[key] === undefined || attributes[key] === '') delete attributes[key];
    }
    return attributes;
  }

  /** The instance for `endpoint`'s project, else the app's own instance once it is initialized. */
  async #instanceForTurn(endpoint: string | undefined): Promise<ApplicationInsights | undefined> {
    const sink = endpoint ? await this.#sinkFor(endpoint) : undefined;
    if (sink) return sink;
    return this.#track.initialized ? this.#track.appInsightsInstance : undefined;
  }

  /** Writes `event` to the App Insights resource for the Foundry project at `endpoint`. */
  async track(endpoint: string | undefined, event: AiTelemetryEvent): Promise<void> {
    try {
      const properties: Record<string, string> = { ...event.properties };
      if (event.operationId) properties[OPERATION_ID] = event.operationId;
      const userId = this.#auth.user?.systemUserID;
      if (userId) properties[AUTH_USER_ID] = userId;

      const sink = endpoint ? await this.#sinkFor(endpoint) : undefined;
      if (sink) {
        this.#ensureInitializer(sink);
        sink.trackEvent({ name: event.name }, properties);
        return;
      }
      const appInstance = this.#track.appInsightsInstance;
      if (appInstance) this.#ensureInitializer(appInstance);
      this.#track.trackEvent(event.name, properties);
    } catch (e) {
      console.warn('AiTelemetry: the event could not be written', e);
    }
  }

  #sinkFor(endpoint: string): Promise<ApplicationInsights | undefined> {
    let sink = this.#sinks.get(endpoint);
    if (!sink) {
      sink = this.#connectionStringFor(endpoint).then(cs => (cs ? this.#instanceFor(cs) : undefined));
      this.#sinks.set(endpoint, sink);
    }
    return sink;
  }

  async #connectionStringFor(endpoint: string): Promise<string | undefined> {
    const saved = (await this.#savedConnectionStrings())[endpoint];
    if (saved) return saved;
    const fromProject = await this.#projectConnectionString(endpoint);
    if (fromProject) {
      await this.#saveConnectionString(endpoint, fromProject);
      return fromProject;
    }
    return this.#orgConnectionString();
  }

  /** The project's `AppInsights` connection string; undefined when the rep cannot read the project's connections. */
  async #projectConnectionString(endpoint: string): Promise<string | undefined> {
    try {
      const token = await this.#authData.getTokenForResource(FOUNDRY_RESOURCE);
      if (!token?.token) return undefined;
      const response = await fetch(`${endpoint}/connections?api-version=${CONNECTIONS_API_VERSION}`, {
        headers: { Authorization: `Bearer ${token.token}` },
      });
      if (!response.ok) return undefined;
      const body = (await response.json()) as { value?: ProjectConnection[] };
      return body.value?.find(c => c.type === 'AppInsights')?.metadata?.ApplicationInsightsConnectionString || undefined;
    } catch {
      return undefined;
    }
  }

  #orgConnectionString(): string | undefined {
    return this.#auth.user?.ioConfigurations
      ?.find(c => c.configName === IoConfigurationFeatureCodes.AI_APP_INSIGHTS_CONNECTION_STRING)
      ?.configValue || undefined;
  }

  async #savedConnectionStrings(): Promise<Record<string, string>> {
    try {
      const doc = (await this.#disk.retrieve(DB_KEY_PREFIXES.AI_APP_INSIGHTS, true)) as ConnectionStringDoc | null;
      return doc?.raw ?? {};
    } catch {
      return {};
    }
  }

  async #saveConnectionString(endpoint: string, connectionString: string): Promise<void> {
    try {
      await this.#disk.updateOrInsert(DB_KEY_PREFIXES.AI_APP_INSIGHTS, (doc: ConnectionStringDoc | null) => ({
        raw: { ...(doc?.raw ?? {}), [endpoint]: connectionString },
      }));
    } catch (e) {
      console.warn('AiTelemetry: the App Insights connection could not be saved', e);
    }
  }

  #instanceFor(connectionString: string): ApplicationInsights {
    let instance = this.#instances.get(connectionString);
    if (!instance) {
      instance = new ApplicationInsights({
        config: {
          connectionString,
          namePrefix: STORAGE_NAME_PREFIX,
          disableAjaxTracking: true,
          disableFetchTracking: true,
          disableExceptionTracking: true,
          disableCookiesUsage: true,
        },
      });
      instance.loadAppInsights();
      this.#instances.set(connectionString, instance);
    }
    return instance;
  }

  /**
   * Registers, once per instance, the initializer that moves the reserved keys onto envelope tags and writes
   * the {@link SDK_OMITTED_ATTRIBUTES} of a span started here onto its item's custom properties.
   */
  #ensureInitializer(instance: ApplicationInsights): void {
    if (this.#initialized.has(instance) || !instance.addTelemetryInitializer) return;
    instance.addTelemetryInitializer((item: TelemetryItem) => {
      const spanId = item?.baseData?.id;
      const omitted = typeof spanId === 'string' ? this.#omittedAttributes.get(spanId) : undefined;
      if (omitted && item.baseData) {
        item.baseData.properties = { ...(item.baseData.properties ?? {}), ...omitted };
        return;
      }
      const bags = [item?.data, item?.baseData?.properties].filter(Boolean) as Record<string, unknown>[];
      const take = (key: string): string | undefined => {
        const value = bags.map(b => b[key]).find(v => typeof v === 'string') as string | undefined;
        for (const b of bags) delete b[key];
        return value;
      };
      const operationId = take(OPERATION_ID);
      const userId = take(AUTH_USER_ID);
      if (!operationId && !userId) return;
      item.tags = item.tags ?? {};
      if (operationId) item.tags['ai.operation.id'] = operationId;
      if (userId) item.tags['ai.user.authUserId'] = userId;
    });
    this.#initialized.add(instance);
  }
}

/**
 * The opt-in content attributes of a turn: `gen_ai.input.messages` with the rep's message,
 * `gen_ai.output.messages` with the final reply, and `com.exeevo.content.truncated`. Each attribute is
 * JSON of at most {@link PROPERTY_VALUE_CHARS} UTF-16 code units; a longer text is cut inside the JSON string,
 * never within a surrogate pair, and the flag is `true`. Empty when the turn has neither text.
 */
export function contentAttributes(question: string | undefined, answer: string | undefined): IOTelAttributes {
  const attributes: IOTelAttributes = {};
  let truncated = false;
  const add = (key: string, text: string | undefined, message: (content: string) => ChatMessage): void => {
    if (!text) return;
    const fitted = fitMessages(text, message);
    attributes[key] = fitted.json;
    truncated ||= fitted.truncated;
  };
  add('gen_ai.input.messages', question, content => ({ role: 'user', parts: [{ type: 'text', content }] }));
  add('gen_ai.output.messages', answer, content => ({ role: 'assistant', parts: [{ type: 'text', content }], finish_reason: 'stop' }));
  if (Object.keys(attributes).length) attributes['com.exeevo.content.truncated'] = truncated;
  return attributes;
}

/**
 * The JSON array holding the one message `message(text)`, with `text` cut to the longest prefix of whole
 * code points that keeps the JSON within {@link PROPERTY_VALUE_CHARS}.
 */
function fitMessages(text: string, message: (content: string) => ChatMessage): { json: string; truncated: boolean } {
  const json = JSON.stringify([message(text)]);
  if (json.length <= PROPERTY_VALUE_CHARS) return { json, truncated: false };
  let budget = PROPERTY_VALUE_CHARS - JSON.stringify([message('')]).length;
  let end = 0;
  for (const codePoint of text) {
    const escaped = JSON.stringify(codePoint).length - 2;
    if (escaped > budget) break;
    budget -= escaped;
    end += codePoint.length;
  }
  return { json: JSON.stringify([message(text.slice(0, end))]), truncated: true };
}

/** The host of a Dataverse environment URL, which names the org; empty when there is none. */
function hostOf(url: string | undefined): string {
  try {
    return url ? new URL(url).host : '';
  } catch {
    return '';
  }
}
